// SPDX-License-Identifier: Elastic-2.0
/**
 * useOnboardingStore — Zustand store for the 6-step onboarding wizard
 * (functional spec § 3, technical spec § 4).
 *
 * Owns:
 *  - `step` cursor (0..5, where 0 = A1 Language)
 *  - per-step user-collected payloads (language, network, location,
 *    privacy, auth)
 *  - mutators for each payload
 *  - `markComplete()` — flips `localStorage.onboarding-complete` so the
 *    `OnboardingGuard` (T-004) lets the user past `/onboarding` on the
 *    next route resolution
 *  - `reset()` — wipes the store + the persisted JSON, used by the
 *    "Reset onboarding" affordance in Settings
 *
 * Persistence:
 *  - We hand-roll a `localStorage.onboarding-state` round-trip instead
 *    of reaching for `zustand/middleware/persist`. It matches the
 *    existing `useTheme` / `useLocale` style in this repo and keeps the
 *    bundle small. Persistence is `try { } catch { }`-guarded so
 *    private-mode Safari / disabled storage degrade to in-memory.
 *  - We persist on every state change via a `subscribe()` listener
 *    registered at module load, NOT inside each setter — keeps mutators
 *    cheap and avoids missing edge cases like `reset()`.
 *
 * Independence from `localStorage.onboarding-complete`:
 *  - The "complete" flag is a separate key, not part of the persisted
 *    state object. That mirrors what `OnboardingGuard` (T-004) reads,
 *    and means clearing the onboarding state never accidentally
 *    re-onboards a finished user. `reset()` clears both keys explicitly.
 */
import { create } from "zustand";

/** localStorage key for the persisted wizard state. */
export const ONBOARDING_STATE_KEY = "onboarding-state";

/** localStorage key read by `OnboardingGuard` (T-004) — set by `markComplete()`. */
export const ONBOARDING_COMPLETE_KEY = "onboarding-complete";

// ── Payload types ─────────────────────────────────────────────────────

/** Per-step payload: A2 network. */
export interface NetworkPayload {
  ssid?: string;
  password?: string;
  ethernet?: boolean;
}

/** Per-step payload: A3 location. */
export interface LocationPayload {
  lat: number;
  lng: number;
  tz: string;
  city: string;
}

/** Per-step payload: A4 privacy. */
export interface PrivacyPayload {
  telemetry: boolean;
  crash: boolean;
  improve: boolean;
}

/** Per-step payload: A5 auth. `mode === null` means the user hasn't picked. */
export interface AuthPayload {
  mode: "cloud" | "local-only" | null;
  paired: boolean;
}

/** Persisted, JSON-serializable slice of the store. */
export interface OnboardingPersisted {
  step: number;
  language: string | null;
  network: NetworkPayload | null;
  location: LocationPayload | null;
  privacy: PrivacyPayload;
  auth: AuthPayload;
}

/** Full store shape (persisted slice + mutators). */
export interface OnboardingState extends OnboardingPersisted {
  setStep: (step: number) => void;
  setLanguage: (language: string | null) => void;
  setNetwork: (network: NetworkPayload | null) => void;
  setLocation: (location: LocationPayload | null) => void;
  setPrivacy: (privacy: PrivacyPayload) => void;
  setAuth: (auth: AuthPayload) => void;
  markComplete: () => void;
  reset: () => void;
}

/** Default state used on first visit and by `reset()`. */
export const DEFAULT_ONBOARDING_STATE: OnboardingPersisted = {
  step: 0,
  language: null,
  network: null,
  location: null,
  // F-342 (ADR-002 Amendment §A.4): telemetry defaults ON for the MVP
  // validation window. crash + improve stay opt-in (out of scope here).
  privacy: { telemetry: true, crash: false, improve: false },
  auth: { mode: null, paired: false },
};

// ── Persistence helpers ───────────────────────────────────────────────

/**
 * Read + JSON-parse the persisted slice. Returns `null` if nothing is
 * stored OR if the value is unparseable / shape-mismatched. We don't
 * try to migrate older shapes — onboarding state is ephemeral by
 * design (the user will just restart from step 0).
 */
function readPersisted(): OnboardingPersisted | null {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(ONBOARDING_STATE_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    const candidate = parsed as Partial<OnboardingPersisted>;
    if (typeof candidate.step !== "number") {
      return null;
    }
    return {
      step: candidate.step,
      language: candidate.language ?? null,
      network: candidate.network ?? null,
      location: candidate.location ?? null,
      privacy: candidate.privacy ?? DEFAULT_ONBOARDING_STATE.privacy,
      auth: candidate.auth ?? DEFAULT_ONBOARDING_STATE.auth,
    };
  } catch {
    // Quota errors / disabled storage / parse failures — treat as absent.
    return null;
  }
}

/** Write the slice (silent on storage failures). */
function writePersisted(slice: OnboardingPersisted): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(ONBOARDING_STATE_KEY, JSON.stringify(slice));
  } catch {
    // Ignore: storage quota / disabled / private mode.
  }
}

/** Remove the persisted slice (silent on failure). */
function clearPersisted(): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return;
  }
  try {
    window.localStorage.removeItem(ONBOARDING_STATE_KEY);
  } catch {
    // Ignore.
  }
}

/** Write the "I'm done" flag read by `OnboardingGuard` (T-004). */
function writeCompleteFlag(value: boolean): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return;
  }
  try {
    if (value) {
      window.localStorage.setItem(ONBOARDING_COMPLETE_KEY, "true");
    } else {
      window.localStorage.removeItem(ONBOARDING_COMPLETE_KEY);
    }
  } catch {
    // Ignore.
  }
}

/** Pick only the persisted fields off the live state for serialization. */
function extractPersisted(state: OnboardingState): OnboardingPersisted {
  return {
    step: state.step,
    language: state.language,
    network: state.network,
    location: state.location,
    privacy: state.privacy,
    auth: state.auth,
  };
}

// ── Store ─────────────────────────────────────────────────────────────

const initialSlice: OnboardingPersisted = readPersisted() ?? DEFAULT_ONBOARDING_STATE;

export const useOnboardingStore = create<OnboardingState>((set) => ({
  ...initialSlice,
  setStep: (step) => set({ step }),
  setLanguage: (language) => set({ language }),
  setNetwork: (network) => set({ network }),
  setLocation: (location) => set({ location }),
  setPrivacy: (privacy) => set({ privacy }),
  setAuth: (auth) => set({ auth }),
  markComplete: () => {
    writeCompleteFlag(true);
    // We do NOT clear `onboarding-state` here — leaving it allows
    // a manual `reset()` from Settings to drop straight back to step 0
    // with no stale fields, while a redirect-from-A6 user simply never
    // re-reads it because the guard short-circuits.
  },
  reset: () => {
    writeCompleteFlag(false);
    clearPersisted();
    set({ ...DEFAULT_ONBOARDING_STATE });
  },
}));

// Persist on every state change. Registered once at module load.
// We compare the persisted slices to skip no-op writes when the change
// was confined to a function reference (mutator identity is stable, so
// this is mostly belt-and-braces).
let lastSerialized = JSON.stringify(extractPersisted(useOnboardingStore.getState()));
useOnboardingStore.subscribe((state) => {
  const next = JSON.stringify(extractPersisted(state));
  if (next !== lastSerialized) {
    lastSerialized = next;
    writePersisted(extractPersisted(state));
  }
});

// Write the initial slice so a fresh user has a persisted record from
// the first interaction onward. Idempotent if storage already contains
// the same value.
writePersisted(initialSlice);
